import { EventEmitter } from 'eventemitter3';
import type { AlignmentData, AlignmentQueueItem } from '../../types/message.types';

/**
 * Alignment Service Event Map
 */
export type AlignmentServiceEventMap = {
  'alignment-update': (data: AlignmentUpdateData) => void;
  'alignment-started': () => void;
  'alignment-stopped': () => void;
  'chunk-complete': (chunkIndex: number) => void;
};

/**
 * Alignment Update Data
 */
export interface AlignmentUpdateData {
  messageId: string;
  text: string;
  charIndex: number;
  isInterrupted?: boolean;
}

/**
 * Service that handles text-to-speech alignment and character-by-character reveal.
 * Synchronizes text display with audio playback for a natural reading experience.
 */
export class AlignmentService extends EventEmitter<AlignmentServiceEventMap> {
  private static instance: AlignmentService | null = null;
  private instanceId: number;
  private instanceNumber: number;
  
  // Alignment queue to match audio chunks
  private alignmentQueue: AlignmentQueueItem[] = [];
  
  // Playback state
  private alignmentInterval: NodeJS.Timeout | null = null;
  private currentPlayingChunkIndex: number = 0;
  private currentChunkStartTime: number = 0;
  private totalCharactersPlayed: number = 0;
  private fullAccumulatedText: string = '';
  private currentMessageId: string | null = null;
  
  // Debounce tracking for alignment events
  private lastEmittedCharIndex: number = -1;
  private lastEmittedMessageId: string | null = null;
  
  // Configuration
  private readonly UPDATE_INTERVAL_MS = 10; // Update alignment every 10ms for smooth reveal
  
  private constructor(instanceNumber: number) {
    super();
    this.instanceNumber = instanceNumber;
    this.instanceId = instanceNumber;  // Keep for backward compatibility
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(): AlignmentService {
    if (!AlignmentService.instance) {
      const instanceNumber = 1;
      AlignmentService.instance = new AlignmentService(instanceNumber);
    }
    return AlignmentService.instance;
  }
  
  /**
   * Queue alignment data for a chunk
   */
  public queueAlignment(
    alignment: AlignmentData,
    chunkIndex: number,
    messageId: string
  ): void {
    // If this is a different message, reset the queue
    if (messageId !== this.currentMessageId) {
      this.reset();
      this.currentMessageId = messageId;
      // Reset debounce tracking for new message
      this.lastEmittedCharIndex = -1;
      this.lastEmittedMessageId = null;
    }
    
    // Add to queue
    this.alignmentQueue.push({
      alignment,
      chunkIndex,
      messageId
    });
    
    // Accumulate text
    const chunkText = alignment.chars.join('');
    this.fullAccumulatedText += chunkText;
    
    // If this is the first chunk and playback hasn't started, start it
    if (this.alignmentQueue.length === 1 && !this.alignmentInterval) {
      this.startPlayback();
    }
  }
  
  /**
   * Start alignment playback
   */
  public startPlayback(): void {
    if (this.alignmentQueue.length === 0) return;
    
    // Clear any existing interval
    this.stopPlayback();
    
    // Reset playback state
    this.currentPlayingChunkIndex = 0;
    this.currentChunkStartTime = Date.now();
    this.totalCharactersPlayed = 0;
    
    // Reset debounce tracking
    this.lastEmittedCharIndex = -1;
    this.lastEmittedMessageId = null;
    
    // Emit started event
    this.emit('alignment-started');
    
    // Start playback interval
    this.alignmentInterval = setInterval(() => {
      this.processPlayback();
    }, this.UPDATE_INTERVAL_MS);
  }
  
  /**
   * Process playback update
   */
  private processPlayback(): void {
    // Check if we have chunks to play
    if (this.currentPlayingChunkIndex >= this.alignmentQueue.length) {
      // No more chunks to play yet, keep interval running
      // (more chunks might be added as they arrive)
      return;
    }
    
    const currentChunk = this.alignmentQueue[this.currentPlayingChunkIndex];

    if (currentChunk == null) {
      return;
    }

    const elapsed = Date.now() - this.currentChunkStartTime;

    // Calculate current character index within the chunk
    const localCharIndex = this.getCurrentChunkCharIndex();
    const globalCharIndex = this.totalCharactersPlayed + localCharIndex;

    // Only emit if position has actually changed (debounce)
    if (globalCharIndex !== this.lastEmittedCharIndex ||
        currentChunk.messageId !== this.lastEmittedMessageId) {

      this.emit('alignment-update', {
        messageId: currentChunk.messageId,
        text: this.fullAccumulatedText,
        charIndex: globalCharIndex
      });

      this.lastEmittedCharIndex = globalCharIndex;
      this.lastEmittedMessageId = currentChunk.messageId;
    }

    // Check if current chunk is complete
    const charStartTimes = currentChunk.alignment.charStartTimesMs;
    const charDurations = currentChunk.alignment.charDurationsMs;

    if (charStartTimes.length > 0) {
      const lastCharIndex = charStartTimes.length - 1;
      const lastStartTime = charStartTimes[lastCharIndex];
      const lastDuration = charDurations[lastCharIndex];
      const lastCharTime = (lastStartTime ?? 0) + (lastDuration ?? 0);
      
      if (elapsed >= lastCharTime) {
        // Current chunk complete, move to next
        this.totalCharactersPlayed += currentChunk.alignment.chars.length;
        this.emit('chunk-complete', this.currentPlayingChunkIndex);
        
        this.currentPlayingChunkIndex++;
        this.currentChunkStartTime = Date.now();
      }
    }
  }
  
  /**
   * Get current character index within the current chunk
   */
  private getCurrentChunkCharIndex(): number {
    if (!this.alignmentQueue.length || this.currentPlayingChunkIndex >= this.alignmentQueue.length) {
      return 0;
    }

    const currentChunk = this.alignmentQueue[this.currentPlayingChunkIndex];

    if (currentChunk == null) {
      return 0;
    }

    const elapsed = Date.now() - this.currentChunkStartTime;
    const charStartTimes = currentChunk.alignment.charStartTimesMs;
    let charIndex = 0;

    // Find current character in this chunk based on elapsed time
    while (charIndex < charStartTimes.length &&
           (charStartTimes[charIndex] ?? 0) <= elapsed) {
      charIndex++;
    }

    return Math.max(0, charIndex - 1);
  }
  
  /**
   * Get current playback position for interruption handling
   */
  public getCurrentPosition(): { 
    charIndex: number; 
    textHeard: string; 
    totalChars: number;
    percentageHeard: number;
  } | null {
    if (!this.currentMessageId || !this.fullAccumulatedText) {
      return null;
    }
    
    const currentCharIndex = this.totalCharactersPlayed + this.getCurrentChunkCharIndex();
    const textHeard = this.fullAccumulatedText.substring(0, currentCharIndex);
    const totalChars = this.fullAccumulatedText.length;
    const percentageHeard = totalChars > 0 ? Math.round((currentCharIndex / totalChars) * 100) : 0;
    
    return {
      charIndex: currentCharIndex,
      textHeard,
      totalChars,
      percentageHeard
    };
  }
  
  /**
   * Stop alignment playback
   */
  public stopPlayback(interrupted: boolean = false): void {
    // Clear the interval
    if (this.alignmentInterval) {
      clearInterval(this.alignmentInterval);
      this.alignmentInterval = null;
    }
    
    // Emit final alignment position before clearing
    if (this.currentMessageId && this.fullAccumulatedText) {
      const finalCharIndex = this.totalCharactersPlayed + this.getCurrentChunkCharIndex();
      // Only emit the text that was actually played
      const textHeard = this.fullAccumulatedText.substring(0, finalCharIndex);
      
      this.emit('alignment-update', {
        messageId: this.currentMessageId,
        text: textHeard,
        charIndex: finalCharIndex,
        isInterrupted: interrupted
      });
    }
    
    // Emit stopped event
    this.emit('alignment-stopped');
  }
  
  /**
   * Subscribe to an event with automatic cleanup
   * Returns an unsubscribe function
   */
  public subscribe<K extends keyof AlignmentServiceEventMap>(
    event: K,
    handler: AlignmentServiceEventMap[K]
  ): () => void {
    // Cast handler to the expected type for EventEmitter
    const typedHandler = handler as (...args: Array<unknown>) => void;

    // Add the event listener
    this.on(event, typedHandler);

    // Return unsubscribe function
    return () => {
      this.off(event, typedHandler);
    };
  }
  
  /**
   * Reset alignment state (enhanced for Reset Pattern)
   */
  public reset(): void {
    // 1. STOP PLAYBACK
    if (this.alignmentInterval) {
      this.stopPlayback(false);
    }
    
    // 2. CLEAR QUEUE
    this.alignmentQueue = [];
    
    // 3. RESET PLAYBACK STATE
    this.currentPlayingChunkIndex = 0;
    this.currentChunkStartTime = 0;
    this.totalCharactersPlayed = 0;
    
    // 4. CLEAR TEXT STATE
    this.fullAccumulatedText = '';
    this.currentMessageId = null;
    
    // 5. RESET DEBOUNCE TRACKING
    this.lastEmittedCharIndex = -1;
    this.lastEmittedMessageId = null;
    
    // 5. DO NOT CLEAR EVENT LISTENERS - Keep them for continuous operation
    // The ServiceCoordinator sets up listeners once and expects them to persist
  }
  
  /**
   * Check if the service is currently in use
   */
  public isInUse(): boolean {
    return this.isActive() || this.hasQueuedData();
  }
  
  /**
   * Destroy the singleton instance (only for app unmount)
   */
  public static destroy(): void {
    if (AlignmentService.instance) {
      AlignmentService.instance.reset();
      // Clear all listeners when destroying the singleton
      AlignmentService.instance.removeAllListeners();
      AlignmentService.instance = null;
    }
  }
  
  /**
   * Get current playback state
   */
  public getPlaybackState(): {
    isPlaying: boolean;
    messageId: string | null;
    fullText: string;
    charactersPlayed: number;
    totalCharacters: number;
    percentagePlayed: number;
  } {
    const charactersPlayed = this.totalCharactersPlayed + this.getCurrentChunkCharIndex();
    const totalCharacters = this.fullAccumulatedText.length;
    
    return {
      isPlaying: this.alignmentInterval !== null,
      messageId: this.currentMessageId,
      fullText: this.fullAccumulatedText,
      charactersPlayed,
      totalCharacters,
      percentagePlayed: totalCharacters > 0 
        ? Math.round((charactersPlayed / totalCharacters) * 100)
        : 0
    };
  }
  
  /**
   * Get text heard so far
   */
  public getTextHeard(): string {
    const charactersHeard = this.totalCharactersPlayed + this.getCurrentChunkCharIndex();
    return this.fullAccumulatedText.substring(0, charactersHeard);
  }
  
  /**
   * Emit current alignment state
   */
  private emitCurrentState(): void {
    if (!this.currentMessageId) return;
    
    const globalCharIndex = this.totalCharactersPlayed + this.getCurrentChunkCharIndex();
    
    // Removed verbose logging - fires every 10ms during playback
    this.emit('alignment-update', {
      messageId: this.currentMessageId,
      text: this.fullAccumulatedText,
      charIndex: globalCharIndex
    });
  }
  
  /**
   * Check if alignment is currently active
   */
  public isActive(): boolean {
    return this.alignmentInterval !== null;
  }
  
  /**
   * Check if there is queued alignment data
   */
  public hasQueuedData(): boolean {
    return this.alignmentQueue.length > 0;
  }
  
  /**
   * Get accumulated text for current message
   */
  public getAccumulatedText(): string {
    return this.fullAccumulatedText;
  }
  
  /**
   * Get current message ID
   */
  public getCurrentMessageId(): string | null {
    return this.currentMessageId;
  }
}